import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  ReactNode,
} from "react";
import { useSocket } from "./SocketContext";
import { IUser } from "../types/user";
import { loadUsers } from "../api/user";

interface UserContextType {
  userList: IUser[];
  loading: boolean;
  loadAllUsers: () => Promise<void>;
}

const UserContext = createContext<UserContextType | null>(null);

export const UsersProvider = ({ children }: { children: ReactNode }) => {
  const [userList, setUserList] = useState<IUser[]>([]);
  const [loading, setLoading] = useState(false);

  /* ---------------- LOAD ---------------- */
  const loadAllUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await loadUsers();
      setUserList(response ?? []);
    } catch (err) {
      console.error("[UserContext] loadAllUsers", err);
    } finally {
      setLoading(false);
    }
  }, []);

  // 👇 CARREGA AUTOMATICAMENTE
  React.useEffect(() => {
    loadAllUsers();
  }, [loadAllUsers]);

  /* ---------------- SIGNALR (opcional futuro) ---------------- */
  const { subscribeEvent } = useSocket();

  React.useEffect(() => {
    const unsub = subscribeEvent<{ users: IUser[] }>(
      "UsersUpdated",
      (data) => {
        console.log("[UserContext] UsersUpdated", data.users);
        setUserList(data.users);
      }
    );

    return () => unsub();
  }, [subscribeEvent]);

  const value = useMemo(
    () => ({
      userList,
      loading,
      loadAllUsers,
    }),
    [userList, loading, loadAllUsers]
  );

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

export const useUsers = () => {
  const ctx = useContext(UserContext);
  if (!ctx) {
    throw new Error("useUsers must be used inside UsersProvider");
  }
  return ctx;
};
